import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import hotkeys from "hotkeys-js"

// Connects to data-controller="markdown-editor"
export default class extends Controller {
  static targets = ["input", "editor", "preview", "show", "title", "fileField", "imagesField"]

  connect() {
    this.previewTarget.classList.add("hidden")
    if (this.hasTitleTarget && !this.titleTarget.value) {
      this.titleTarget.focus()
    }

    this._prevHotkeysFilter = hotkeys.filter
    // Pozwolenie na skróty markdown editora tylko gdy focus jest na inputTarget
    // Dla innych elementów używamy oryginalnego filtra
    hotkeys.filter = function(e) {
      if (document.activeElement === this.inputTarget) {
        return true
      }
      return this._prevHotkeysFilter(e)
    }.bind(this)
    const keymap = [
      ["ctrl+b,meta+b", () => this.insertBold()],
      ["ctrl+i,meta+i", () => this.insertItalic()],
      ["ctrl+shift+s,meta+shift+s", () => this.insertStrikethrough()],
      ["ctrl+shift+c,meta+shift+c", () => this.insertCode()],
      ["ctrl+shift+h,meta+shift+h", () => this.insertHeader()],
      ["ctrl+shift+l,meta+shift+l", () => this.insertList()],
      ["ctrl+shift+7,meta+shift+7", () => this.insertNumberedList()],
      ["ctrl+shift+i,meta+shift+i", () => this.insertImage()],
      // enter, shift+enter, tab, shift+tab - zrobione w input event, na podstawie autoupdate
      // key-up, key-down - zrobione w input event, na podstawie title
    ]

    this._hotkeys = []
    keymap.forEach(([combo, fn]) => {
      const handler = (e, handler) => {
        e.preventDefault()
        fn(e, handler)
      }
      hotkeys(combo, this.inputTarget, handler)
      this._hotkeys.push(combo)
    })
  }

  disconnect() {
    if (this._hotkeys) {
      this._hotkeys.forEach(combo => hotkeys.unbind(combo))
    }
    hotkeys.filter = this._prevHotkeysFilter
  }

  insert(type) {
    const selectionStart = this.inputTarget.selectionStart
    const selectionEnd = this.inputTarget.selectionEnd

    const v = this.inputTarget.value
    const textBefore = v.substring(0, this.inputTarget.selectionStart)
    const textSelected = v.substring(this.inputTarget.selectionStart, this.inputTarget.selectionEnd)
    const textAfter = v.substring(this.inputTarget.selectionEnd)

    let changedText
    const lines = textSelected.split("\n")
    const startsEndsWithType = (line) => line.startsWith(type) && line.endsWith(type)

    if (lines.every(startsEndsWithType)) {
      changedText = lines.map(function (line) { return line.substring(type.length, line.length - type.length) }).join("\n")
      this.inputTarget.value = textBefore + changedText + textAfter
      this.inputTarget.selectionStart = selectionStart
      this.inputTarget.selectionEnd = selectionEnd - 2 * type.length * lines.length
    } else {
      changedText = lines.map(function (line) { return type + line + type }).join("\n")
      this.inputTarget.value = textBefore + changedText + textAfter
      this.inputTarget.selectionStart = selectionStart
      this.inputTarget.selectionEnd = selectionEnd + 2 * type.length * lines.length
    }

    this.inputTarget.focus()
  }

  startOfLineIndex(v, selectionStart) {
    for (let i = selectionStart - 1; i >= 0; i--) {
      if (v[i] === "\n") { return i + 1 }
    }
    return 0
  }

  endOfLineIndex(v, selectionEnd) {
    for (let i = selectionEnd; i <= v.length; i++) {
      if (v[i] === "\n") { return i }
    }
    return v.length
  }

  // code -1 -> tylko usuwanie
  // code 0 -> usuwanie i dodawanie
  // code 1 -> tylko dodawanie
  insertAtStart(type, code = 0) {
    const selectionStart = this.inputTarget.selectionStart
    const selectionEnd = this.inputTarget.selectionEnd
    const v = this.inputTarget.value

    const firstElementIndex = this.startOfLineIndex(v, selectionStart)
    const LastElementIndex = this.endOfLineIndex(v, selectionEnd)

    const textBefore = v.substring(0, firstElementIndex)
    const textModified = v.substring(firstElementIndex, LastElementIndex)
    const textAfter = v.substring(LastElementIndex)

    let changedText
    const lines = textModified.split("\n")
    const startsWithType = (line) => line.startsWith(type)

    if (code !== 1 && lines.every(startsWithType)) {
      changedText = lines.map(function (line) { return line.slice(type.length) }).join("\n")
      this.inputTarget.value = textBefore + changedText + textAfter
      this.inputTarget.selectionStart = Math.max(selectionStart - type.length, firstElementIndex)
      this.inputTarget.selectionEnd = selectionEnd - type.length * lines.length
    } else if (code !== -1) {
      changedText = lines.map(function (line) { return type + line }).join("\n")
      this.inputTarget.value = textBefore + changedText + textAfter
      this.inputTarget.selectionStart = selectionStart + type.length
      this.inputTarget.selectionEnd = selectionEnd + type.length * lines.length
    }

    this.inputTarget.focus()
  }

  insertNumberAtStart(startFrom = 1) {
    const selectionStart = this.inputTarget.selectionStart
    const selectionEnd = this.inputTarget.selectionEnd

    const v = this.inputTarget.value

    const firstElementIndex = this.startOfLineIndex(v, selectionStart)
    const LastElementIndex = this.endOfLineIndex(v, selectionEnd)

    const textBefore = v.substring(0, firstElementIndex)
    const textModified = v.substring(firstElementIndex, LastElementIndex)
    const textAfter = v.substring(LastElementIndex)

    let changedText
    const lines = textModified.split("\n")
    const startsWithNumber = (line) => line.match(/^(\d+\. )/)
    let lengthChange = 0

    if (lines.every(startsWithNumber)) {
      changedText = lines.map(function (line) {
        const typeLength = line.match(/^(\d+\. )/)[0].length
        lengthChange += typeLength
        return line.slice(typeLength)
      }).join("\n")
      this.inputTarget.value = textBefore + changedText + textAfter

      this.inputTarget.selectionStart = Math.max(selectionStart - startFrom.toString().length - 2, firstElementIndex) // "x. ".length
      this.inputTarget.selectionEnd = selectionEnd - lengthChange
    } else {
      let pointNumber = startFrom - 1
      changedText = lines.map(function (line) {
        pointNumber++
        const addedPrefix = pointNumber.toString() + ". "
        lengthChange += addedPrefix.length
        return (addedPrefix + line)
      }).join("\n")

      this.inputTarget.value = textBefore + changedText + textAfter
      this.inputTarget.selectionStart = selectionStart + startFrom.toString().length + 2 // "x. ".length
      this.inputTarget.selectionEnd = selectionEnd + lengthChange
    }

    this.inputTarget.focus()
  }

  insertAtEnd(type) {
    const selectionStart = this.inputTarget.selectionStart
    const selectionEnd = this.inputTarget.selectionEnd
    const v = this.inputTarget.value
    const indexBefore = 0
    const indexAfter = v.length + this.inputTarget.selectionEnd
    const textSelected = v.substring(indexBefore, indexAfter)
    if (textSelected.indexOf(type) === v.length - type.length) {
      this.inputTarget.selectionStart = selectionStart - type.length
      this.inputTarget.selectionEnd = selectionEnd - type.length
    } else {
      this.inputTarget.value = textSelected + " " + type
      this.inputTarget.selectionStart = v.length + type.length + 1
      this.inputTarget.selectionEnd = v.length + type.length + 1
    }

    this.inputTarget.focus()
  }

  insertAtCursor(type) {
    const selectionStart = this.inputTarget.selectionStart

    const v = this.inputTarget.value
    const textBefore = v.substring(0, this.inputTarget.selectionStart)
    // const textSelected = v.substring(this.inputTarget.selectionStart, this.inputTarget.selectionEnd)
    const textAfter = v.substring(this.inputTarget.selectionEnd, v.length)

    this.inputTarget.value = textBefore + type + textAfter
    this.inputTarget.selectionStart = selectionStart + type.length

    this.inputTarget.selectionEnd = this.inputTarget.selectionStart
    this.inputTarget.focus()
  }

  updateText(event) {
    function updateNextLines(startingNumber, v, selectionEnd) {
      let line = ""
      const result = {
        textModified: "",
        lastModified: 0
      }
      let textModified = ""
      let currentNumber = startingNumber

      for (let i = selectionEnd + 1; i <= v.length; i++) {
        if (v[i] === "\n" || i === v.length) {
          if (line.match(/^(\d+)(\. .*)$/)) {
            const oldNumber = parseInt(line.match(/^(\d+)(\. .*)$/)[0])
            const oldLength = oldNumber.toString().length

            line = line.slice(oldLength)
            line = currentNumber + line
            currentNumber++
          }
          textModified += (line + "\n")

          if (line.length === 0 || i === v.length) {
            result.textModified = textModified
            result.lastModified = i

            return result
          }
          line = ""
        } else {
          line += v[i]
        }
      }
      return null
    }
    if (event.code === "Enter") {
      let lastLine = this.inputTarget.value.substring(0, this.inputTarget.selectionStart - 1)
      lastLine = lastLine.substring(lastLine.lastIndexOf("\n") + 1, lastLine.length)

      const matchNumbered = lastLine.match(/^(\d+)(\. .+)$/)
      const matchList = lastLine.match(/^-( .+)$/)

      if (matchNumbered && matchNumbered.length > 2) {
        const number = parseInt(matchNumbered[1]) + 1
        this.insertAtStart(number + ". ")

        const v = this.inputTarget.value
        const selectionStart = this.inputTarget.selectionStart
        const selectionEnd = this.inputTarget.selectionEnd

        const textBefore = v.substring(0, selectionStart + 1)
        const update = updateNextLines(number + 1, v, selectionEnd)
        const textModified = update.textModified
        const textAfter = v.substring(update.lastModified + 1, v.length)

        this.inputTarget.value = textBefore + textModified + textAfter
        this.inputTarget.selectionStart = selectionStart
        this.inputTarget.selectionEnd = selectionEnd
      } else if (matchList) {
        this.insertAtStart("- ")
      }
    }
    this.inputTarget.focus()
  }

  insertTab(event) {
    if (event.key === "Tab") {
      event.preventDefault()
      const code = event.shiftKey ? -1 : 1 // -1 -> usuwanie tabów, 1 -> dodawanie
      this.insertAtStart("\t", code)
    }
  }

  insertHeader() {
    this.insertAtStart("### ")
  }

  insertBold() {
    this.insert("**")
  }

  insertItalic() {
    this.insert("_")
  }

  insertStrikethrough() {
    this.insert("~~")
  }

  insertCode() {
    this.insert("`")
  }

  insertList() {
    this.insertAtStart("- ")
  }

  insertNumberedList() {
    this.insertNumberAtStart()
  }

  insertImage() {
    const fileField = this.fileFieldTarget
    fileField.click()
    fileField.addEventListener("change", this.uploadFile.bind(this), false)
    // replacing filefield to remove event listeners
    fileField.replaceWith(fileField.cloneNode())
  }

  insertAttachmentImage(element) {
    const { alt, filename, title } = element.detail
    this.insertAtCursor(this.imgTemplate(alt, filename, title))
    this.element.scrollIntoView()
  }

  moveToTitle(event) {
    // przejdź do tytułu tylko z pierwszej linijki treści
    if (event.key === "ArrowUp" || event.keyCode === 38) {
      // Sprawdź czy jesteśmy w pierwszej linii (przed kursorem nie ma znaku nowej linii)
      const textBefore = this.inputTarget.value.substring(0, this.inputTarget.selectionStart)
      const isFirstLine = !textBefore.includes("\n")

      if (isFirstLine && this.hasTitleTarget) {
        event.preventDefault()
        this.titleTarget.focus()
        // Ustaw kursor na końcu pola title
        const len = this.titleTarget.value.length
        this.titleTarget.setSelectionRange(len, len)
      }
    }
  }

  moveToContent(event) {
    if (event.keyCode === 40 || event.code === "Enter") {
      event.preventDefault()
      this.inputTarget.focus()
    }
  }

  imgTemplate(alt, filename, title) {
    if (alt.length === 0) {
      alt = "opis"
    }
    if (title.length === 0) {
      title = "tytul"
    }
    return `![${alt}](${filename} "${title}")`
  }

  uploadFile(event) {
    const fileField = this.fileFieldTarget
    const imagesField = this.imagesFieldTarget
    const uploadPath = fileField.dataset.directUploadUrl + "?extended=true&in_text=true"
    // let createAttachmentPath = fileField.dataset.createAttachmentUrl
    const fileList = event.target.files
    console.log(fileList)
    const file = fileList[fileList.length - 1]
    const upload = new DirectUpload(file, uploadPath)

    upload.create((error, blob) => {
      if (error) {
        console.log("upload error", error)
      } else {
        console.log("upload success")
        this.insertAtCursor(this.imgTemplate("opis", `${blob.filename}`, "tytul"))
        imagesField.setAttribute("value", `${imagesField.value ? imagesField.value + "," : ""}${blob.signed_id}`)
      }
    })
  }
}
